import { ActionTypes } from "./actions";

export default function QuestionTableReducer(state = {}, action) {
  switch (action.type) {
    // If the QuestionTable hint button is selected, set the showHint state to true.
    case ActionTypes.QUESTION_TABLE_SHOW_HINT: {
      const componentID = action.payload.componentID;
      const currentComponentState = state[componentID] || {};
      currentComponentState.showHint = action.payload.showHint;

      return {
        ...state,
        [componentID]: currentComponentState,
      };
    }

    // If the QuestionTable hint button is selected, set the showHint state to true.
    case ActionTypes.QUESTION_TABLE_SHOW_NEXT_HINT: {
      const componentID = action.payload.componentID;
      const currentComponentState = state[componentID] || {};
      currentComponentState.hintIndex++;

      return {
        ...state,
        [componentID]: currentComponentState,
      };
    }

    // If a QuestionTable cell is changed, update the state to reflect the selected option for the given component.
    case ActionTypes.QUESTION_TABLE_CELL_CHANGE: {
      // Payload values
      const [rowId, colId] = action.payload.id
        .split("_")
        .map((id) => parseInt(id));
      const value = action.payload.value;
      const answer = action.payload.answer;
      const disabled = action.payload.disabled;
      const numberOfInputs = action.payload.numberOfInputs;
      const feedback = action.payload.feedback;
      const options = action.payload.options;
      const unique = action.payload.unique;
      // Current question state
      const componentID = action.payload.componentID;
      const currentComponentState = state[componentID] || {};
      let responses = Object.prototype.hasOwnProperty.call(
        currentComponentState,
        "responses"
      )
        ? currentComponentState.responses
        : [];

      // Existing response index
      const responseIndex = responses.findIndex((response) => {
        return response.rowId == rowId && response.colId == colId;
      });

      // Response object with inputted values
      const responseObject = {
        rowId,
        colId,
        value,
        answer,
        showFeedback: false,
        disabled,
        feedback,
        options,
        unique,
      };

      // If existing response index exists, update; otherwise create new response object.
      if (responseIndex !== -1) {
        if (
          (typeof value === "number" && isNaN(value)) ||
          (typeof value === "string" && value === null)
        ) {
          responseObject.value = "";
        }
        responses = responses.map((response, index) =>
          index === responseIndex ? responseObject : response
        );
      } else {
        if (
          (typeof value === "number" && !isNaN(value)) ||
          (typeof value === "string" && value !== null) ||
          responseObject.options
        ) {
          // If value is not NaN add response to array.
          responses = [...responses, responseObject];
        }
      }

      // If the number of responses matches the number of inputs, enable / disable the submit button
      //let allInputsFilled = responses.filter((response) => (!response.disabled && !response.showFeedback) || (response.disabled && response.showFeedback)).length == numberOfInputs;

      const allInputsFilled = responses.length == numberOfInputs;

      // Return amended state
      return {
        ...state,
        [action.payload.componentID]: {
          ...state[action.payload.componentID],
          submitDisabled: !allInputsFilled,
          responses: responses,
        },
      };
    }

    // When the QuestionTable component is submitted, update the disabled list with the currently selected option (if present).
    case ActionTypes.QUESTION_TABLE_SUBMIT: {
      const hints = [];

      const componentID = action.payload;
      const currentComponentState = state[componentID] || {};
      const responses = Array.isArray(currentComponentState.responses)
        ? currentComponentState.responses
        : [];

      const uniqueResponses = responses
        .filter((item) => item.unique === true)
        .map((item) => item.value);

      const isCorrect =
        responses.length > 0 &&
        responses.every((response) => {
          if (Object.prototype.hasOwnProperty.call(response, "answer")) {
            if (
              uniqueResponses.includes(response.value) &&
              uniqueResponses.filter((item) => item === response.value).length >
                1
            ) {
              return false;
            } else if (typeof response.answer === "string") {
              return (
                response.answer.toLowerCase() === response.value.toLowerCase()
              );
            } else if (typeof response.answer === "number") {
              return response.answer == response.value;
            } else if (
              typeof response.answer === "object" &&
              response.answer !== null &&
              Object.hasOwn(response.answer, "min")
            ) {
              return response.value >= response.answer.min;
            } else if (
              typeof response.answer === "object" &&
              response.answer !== null &&
              Object.hasOwn(response.answer, "max")
            ) {
              return response.value <= response.answer.max;
            } else if (Array.isArray(response.answer)) {
              return response.answer.includes(response.value);
            }
          }
          return true;
        });

      const addHintIfFeedback = (item) => {
        if (item.feedback) {
          hints.push({
            rowId: item.rowId,
            colId: item.colId,
            feedback: item.feedback,
          });
        }
      };

      const isDuplicateValue = (item) => {
        return (
          uniqueResponses.includes(item.value) &&
          uniqueResponses.filter((res) => res === item.value).length > 1
        );
      };

      const isCorrectAnswer = (item) => {
        if (typeof item.answer === "string") {
          return item.answer.toLowerCase() === item.value.toLowerCase();
        }
        if (typeof item.answer === "number") {
          return item.answer == item.value;
        }
        if (typeof item.answer === "object" && item.answer !== null) {
          if (Object.hasOwn(item.answer, "min")) {
            return item.value >= item.answer.min;
          }
          if (Object.hasOwn(item.answer, "max")) {
            return item.value <= item.answer.max;
          }
        }
        if (Array.isArray(item.answer)) {
          return item.answer.includes(item.value);
        }
        return true;
      };

      const disabledResponses = responses.map((item) => {
        const updatedItem = { ...item, showFeedback: true };

        if (isDuplicateValue(item)) {
          addHintIfFeedback(item);
        } else if (isCorrectAnswer(item)) {
          updatedItem.disabled = true;
        } else {
          addHintIfFeedback(item);
        }

        return updatedItem;
      });

      //sort hints in readable manner
      hints.sort((a, b) => {
        if (a.rowId !== b.rowId) {
          return a.rowId - b.rowId;
        } else {
          return a.colId - b.colId;
        }
      });

      return {
        ...state,
        [componentID]: {
          ...currentComponentState,
          submitDisabled: true,
          responses: disabledResponses,
          correct: isCorrect,
          hints: hints,
          hintIndex: 0,
        },
      };
    }

    case ActionTypes.QUESTION_TABLE_OVERRIDE_CELL_DATA: {
      const componentID = action.payload.componentID;
      const [rowId, colId] = action.payload.id
        .split("_")
        .map((id) => parseInt(id));
      const currentComponentState = state[componentID] || {};
      const data = [...(currentComponentState.data || [])];
      data.push({
        rowId: rowId,
        colId: colId,
        data: action.payload.data,
      });

      return {
        ...state,
        [componentID]: {
          ...currentComponentState,
          data,
        },
      };
    }
    default:
      return state;
  }
}
